import React, { useState, useEffect } from 'react';
import { useUser } from '../contexts/UserContext';
import {
  db,
  doc,
  setDoc,
  updateDoc,
  deleteDoc,
  collection,
  onSnapshot,
  serverTimestamp,
  firebaseConfig,
  arrayUnion,
  arrayRemove
} from '../firebase';

function UserManagement({ onClose }) {
  const { user, userProfile, isSystemAdmin, canManageSuperAdmins, getRoleDisplayName } = useUser();

  const [allUsers, setAllUsers] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  // New user form
  const [showNewUserForm, setShowNewUserForm] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState('');
  const [newUserPassword, setNewUserPassword] = useState('');
  const [newUserName, setNewUserName] = useState('');
  const [newUserRole, setNewUserRole] = useState('');
  const [creatingUser, setCreatingUser] = useState(false);
  // Portfolio assignments for new user
  const [newUserPortfolios, setNewUserPortfolios] = useState([]);
  const [addPortfolioId, setAddPortfolioId] = useState('');
  const [addPortfolioRole, setAddPortfolioRole] = useState('customer_viewer');

  // Portfolio assignment (for existing users)
  const [assigningUser, setAssigningUser] = useState(null);
  const [selectedPortfolio, setSelectedPortfolio] = useState('');
  const [selectedPortfolioRole, setSelectedPortfolioRole] = useState('customer_viewer');

  // Real-time users listener
  useEffect(() => {
    if (!isSystemAdmin()) return;

    const unsubscribe = onSnapshot(collection(db, 'users'), (snapshot) => {
      const users = [];
      snapshot.forEach((doc) => {
        users.push({ uid: doc.id, ...doc.data() });
      });
      users.sort((a, b) => (a.name || a.email || '').localeCompare(b.name || b.email || ''));
      setAllUsers(users);
      setLoadingUsers(false);
    });

    setLoadingUsers(true);
    return () => unsubscribe();
  }, [isSystemAdmin]);

  // Real-time portfolios listener
  useEffect(() => {
    if (!isSystemAdmin()) return;

    const unsubscribe = onSnapshot(collection(db, 'customer_portfolios'), (snapshot) => {
      const list = [];
      snapshot.forEach((doc) => {
        list.push({ id: doc.id, ...doc.data() });
      });
      list.sort((a, b) => (a.name || '').localeCompare(b.name || ''));
      setPortfolios(list);
    });

    return () => unsubscribe();
  }, [isSystemAdmin]);

  const handleCreateUser = async () => {
    if (!newUserEmail.trim() || !newUserPassword.trim()) {
      alert('Please enter email and password');
      return;
    }

    if (newUserPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }

    // Check role permissions
    if (newUserRole === 'super_admin' && !canManageSuperAdmins()) {
      alert('Only Super Admins can create Super Admin accounts');
      return;
    }

    setCreatingUser(true);
    try {
      // Create user via Firebase Auth REST API
      const response = await fetch(
        `https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=${firebaseConfig.apiKey}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: newUserEmail.trim(),
            password: newUserPassword,
            returnSecureToken: false
          })
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error?.message || 'Failed to create user');
      }

      // Create user document in Firestore with portfolioIds for easy querying
      const portfolioIds = newUserPortfolios.map(p => p.portfolioId);
      await setDoc(doc(db, 'users', data.localId), {
        email: newUserEmail.trim().toLowerCase(),
        name: newUserName.trim() || null,
        role: newUserRole || null,
        portfolioIds: portfolioIds,
        createdAt: serverTimestamp(),
        createdBy: user.uid
      });

      // Add user to selected portfolios
      for (const assignment of newUserPortfolios) {
        const portfolio = portfolios.find(p => p.id === assignment.portfolioId);
        if (portfolio) {
          const portfolioRef = doc(db, 'customer_portfolios', assignment.portfolioId);
          const newMembers = {
            ...portfolio.members,
            [data.localId]: {
              role: assignment.role,
              joinedAt: serverTimestamp()
            }
          };
          await updateDoc(portfolioRef, { members: newMembers });
        }
      }

      // Send welcome/password reset email
      try {
        await fetch(
          `https://identitytoolkit.googleapis.com/v1/accounts:sendOobCode?key=${firebaseConfig.apiKey}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              requestType: 'PASSWORD_RESET',
              email: newUserEmail.trim()
            })
          }
        );
      } catch (emailError) {
        console.error('Failed to send welcome email:', emailError);
      }

      alert(`User ${newUserEmail} created successfully! A welcome email has been sent.${newUserPortfolios.length > 0 ? ` Added to ${newUserPortfolios.length} portfolio(s).` : ''}`);
      setShowNewUserForm(false);
      setNewUserEmail('');
      setNewUserPassword('');
      setNewUserName('');
      setNewUserRole('');
      setNewUserPortfolios([]);
      setAddPortfolioId('');
      setAddPortfolioRole('customer_viewer');
    } catch (error) {
      let message = error.message;
      if (message.includes('EMAIL_EXISTS')) {
        message = 'This email is already registered';
      } else if (message.includes('INVALID_EMAIL')) {
        message = 'Invalid email address';
      } else if (message.includes('WEAK_PASSWORD')) {
        message = 'Password is too weak';
      }
      alert('Failed to create user: ' + message);
    }
    setCreatingUser(false);
  };

  const handleChangeSystemRole = async (targetUser, newRole) => {
    // Only super_admin can manage super_admin roles
    if ((newRole === 'super_admin' || targetUser.role === 'super_admin') && !canManageSuperAdmins()) {
      alert('Only Super Admins can manage Super Admin roles');
      return;
    }

    if (targetUser.uid === user.uid) {
      alert("You can't change your own system role");
      return;
    }

    const roleName = newRole ? getRoleDisplayName(newRole) : 'Regular User';
    if (!window.confirm(`Change ${targetUser.name || targetUser.email}'s system role to ${roleName}?`)) {
      return;
    }

    try {
      await updateDoc(doc(db, 'users', targetUser.uid), {
        role: newRole || null
      });
      alert(`${targetUser.name || targetUser.email} is now ${roleName}`);
    } catch (error) {
      alert('Failed to update role: ' + error.message);
    }
  };

  const handleAssignPortfolio = async () => {
    if (!assigningUser || !selectedPortfolio) {
      alert('Please select a portfolio');
      return;
    }

    const portfolio = portfolios.find(p => p.id === selectedPortfolio);
    if (!portfolio) return;

    try {
      // Update portfolio members
      const portfolioRef = doc(db, 'customer_portfolios', selectedPortfolio);
      const newMembers = {
        ...portfolio.members,
        [assigningUser.uid]: {
          role: selectedPortfolioRole,
          joinedAt: serverTimestamp()
        }
      };
      await updateDoc(portfolioRef, { members: newMembers });

      // Update user's portfolioIds array
      const userRef = doc(db, 'users', assigningUser.uid);
      await updateDoc(userRef, {
        portfolioIds: arrayUnion(selectedPortfolio)
      });

      alert(`${assigningUser.name || assigningUser.email} added to ${portfolio.name} as ${getRoleDisplayName(selectedPortfolioRole)}`);
      setAssigningUser(null);
      setSelectedPortfolio('');
      setSelectedPortfolioRole('customer_viewer');
    } catch (error) {
      alert('Failed to assign portfolio: ' + error.message);
    }
  };

  const handleRemoveFromPortfolio = async (targetUser, portfolioId) => {
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (!portfolio) return;

    if (!window.confirm(`Remove ${targetUser.name || targetUser.email} from ${portfolio.name}?`)) {
      return;
    }

    try {
      // Remove from portfolio members
      const portfolioRef = doc(db, 'customer_portfolios', portfolioId);
      const newMembers = { ...portfolio.members };
      delete newMembers[targetUser.uid];
      await updateDoc(portfolioRef, { members: newMembers });

      // Remove from user's portfolioIds array
      const userRef = doc(db, 'users', targetUser.uid);
      await updateDoc(userRef, {
        portfolioIds: arrayRemove(portfolioId)
      });

      alert(`${targetUser.name || targetUser.email} removed from ${portfolio.name}`);
    } catch (error) {
      alert('Failed to remove from portfolio: ' + error.message);
    }
  };

  const handleChangePortfolioRole = async (targetUser, portfolioId, newRole) => {
    const portfolio = portfolios.find(p => p.id === portfolioId);
    if (!portfolio) return;

    try {
      const portfolioRef = doc(db, 'customer_portfolios', portfolioId);
      const newMembers = {
        ...portfolio.members,
        [targetUser.uid]: {
          ...portfolio.members[targetUser.uid],
          role: newRole
        }
      };

      await updateDoc(portfolioRef, { members: newMembers });
    } catch (error) {
      alert('Failed to change role: ' + error.message);
    }
  };

  const handleDeleteUser = async (targetUser) => {
    if (targetUser.uid === user.uid) {
      alert("You can't delete your own account");
      return;
    }

    if (targetUser.role === 'super_admin' && !canManageSuperAdmins()) {
      alert('Only Super Admins can delete Super Admin accounts');
      return;
    }

    if (!window.confirm(`Delete user ${targetUser.name || targetUser.email}? This will remove their data and portfolio memberships.`)) {
      return;
    }

    try {
      // Remove from all portfolios first
      for (const portfolio of portfolios) {
        if (portfolio.members?.[targetUser.uid]) {
          const newMembers = { ...portfolio.members };
          delete newMembers[targetUser.uid];
          await updateDoc(doc(db, 'customer_portfolios', portfolio.id), { members: newMembers });
        }
      }

      // Delete user document
      await deleteDoc(doc(db, 'users', targetUser.uid));
      alert(`User ${targetUser.name || targetUser.email} deleted`);
    } catch (error) {
      alert('Failed to delete user: ' + error.message);
    }
  };

  // Get user's portfolio memberships
  const getUserPortfolios = (targetUser) => {
    return portfolios.filter(p => p.members?.[targetUser.uid]).map(p => ({
      id: p.id,
      name: p.name,
      role: p.members[targetUser.uid].role
    }));
  };

  if (!isSystemAdmin()) {
    return (
      <div className="user-management">
        <div className="management-header">
          <button className="back-btn" onClick={onClose}>Back</button>
          <h1>User Management</h1>
        </div>
        <div className="no-access">
          <p>You don't have permission to access this page.</p>
        </div>
      </div>
    );
  }

  // Separate users by system role
  const superAdmins = allUsers.filter(u => u.role === 'super_admin');
  const admins = allUsers.filter(u => u.role === 'admin');
  const regularUsers = allUsers.filter(u => !u.role || (u.role !== 'super_admin' && u.role !== 'admin'));

  return (
    <div className="user-management">
      <div className="management-header">
        <button className="back-btn" onClick={onClose}>Back</button>
        <h1>User Management</h1>
        <button className="btn-primary" onClick={() => setShowNewUserForm(true)}>
          + Add User
        </button>
      </div>

      <div className="management-content">
        {/* New User Form */}
        {showNewUserForm && (
          <div className="management-section new-user-form">
            <h2>Create New User</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Email *</label>
                <input
                  type="email"
                  value={newUserEmail}
                  onChange={(e) => setNewUserEmail(e.target.value)}
                  placeholder="user@example.com"
                />
              </div>
              <div className="form-group">
                <label>Password *</label>
                <input
                  type="password"
                  value={newUserPassword}
                  onChange={(e) => setNewUserPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                />
              </div>
              <div className="form-group">
                <label>Name</label>
                <input
                  type="text"
                  value={newUserName}
                  onChange={(e) => setNewUserName(e.target.value)}
                  placeholder="Full name (optional)"
                />
              </div>
              <div className="form-group">
                <label>System Role</label>
                <select value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)}>
                  <option value="">None (Portfolio access only)</option>
                  {canManageSuperAdmins() && <option value="super_admin">Super Admin</option>}
                  <option value="admin">Admin</option>
                </select>
                <p className="field-hint">Super Admin/Admin have global access to all portfolios</p>
              </div>
            </div>

            {/* Portfolio Assignments Section */}
            <div className="portfolio-assignments-section">
              <h3>Portfolio Access</h3>
              <p className="hint">Assign this user to one or more portfolios with specific roles.</p>

              <div className="add-portfolio-row">
                <select
                  value={addPortfolioId}
                  onChange={(e) => setAddPortfolioId(e.target.value)}
                  className="portfolio-select"
                >
                  <option value="">Select portfolio...</option>
                  {portfolios
                    .filter(p => !newUserPortfolios.find(np => np.portfolioId === p.id))
                    .map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
                <select
                  value={addPortfolioRole}
                  onChange={(e) => setAddPortfolioRole(e.target.value)}
                  className="role-select"
                >
                  <option value="customer_admin">Portfolio Admin</option>
                  <option value="customer_technician">Contractor</option>
                  <option value="customer_viewer">Viewer</option>
                </select>
                <button
                  className="btn-small assign"
                  onClick={() => {
                    if (addPortfolioId) {
                      setNewUserPortfolios([...newUserPortfolios, {
                        portfolioId: addPortfolioId,
                        role: addPortfolioRole,
                        portfolioName: portfolios.find(p => p.id === addPortfolioId)?.name
                      }]);
                      setAddPortfolioId('');
                      setAddPortfolioRole('customer_viewer');
                    }
                  }}
                  disabled={!addPortfolioId}
                >
                  + Add
                </button>
              </div>

              {newUserPortfolios.length > 0 && (
                <div className="assigned-portfolios">
                  {newUserPortfolios.map((np, idx) => (
                    <div key={np.portfolioId} className="assigned-portfolio-tag">
                      <span className="portfolio-name">{np.portfolioName}</span>
                      <select
                        value={np.role}
                        onChange={(e) => {
                          const updated = [...newUserPortfolios];
                          updated[idx].role = e.target.value;
                          setNewUserPortfolios(updated);
                        }}
                      >
                        <option value="customer_admin">Admin</option>
                        <option value="customer_technician">Contractor</option>
                        <option value="customer_viewer">Viewer</option>
                      </select>
                      <button
                        className="remove-btn"
                        onClick={() => setNewUserPortfolios(newUserPortfolios.filter(p => p.portfolioId !== np.portfolioId))}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="role-descriptions">
                <p><strong>Portfolio Admin:</strong> Full access to portfolio, can manage team members</p>
                <p><strong>Contractor:</strong> Can edit sensors, devices, and settings</p>
                <p><strong>Viewer:</strong> Read-only access to view data</p>
              </div>
            </div>

            <div className="form-actions">
              <button
                className="btn-primary"
                onClick={handleCreateUser}
                disabled={creatingUser}
              >
                {creatingUser ? 'Creating...' : 'Create User'}
              </button>
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowNewUserForm(false);
                  setNewUserEmail('');
                  setNewUserPassword('');
                  setNewUserName('');
                  setNewUserRole('');
                  setNewUserPortfolios([]);
                  setAddPortfolioId('');
                  setAddPortfolioRole('customer_viewer');
                }}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Portfolio Assignment Modal */}
        {assigningUser && (
          <div className="modal-overlay" onClick={() => setAssigningUser(null)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <h3>Assign {assigningUser.name || assigningUser.email} to Portfolio</h3>
              <div className="form-group">
                <label>Portfolio</label>
                <select value={selectedPortfolio} onChange={(e) => setSelectedPortfolio(e.target.value)}>
                  <option value="">Select portfolio...</option>
                  {portfolios
                    .filter(p => !p.members?.[assigningUser.uid])
                    .map(p => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                </select>
              </div>
              <div className="form-group">
                <label>Portfolio Role</label>
                <select value={selectedPortfolioRole} onChange={(e) => setSelectedPortfolioRole(e.target.value)}>
                  <option value="customer_admin">Portfolio Admin</option>
                  <option value="customer_technician">Contractor</option>
                  <option value="customer_viewer">Viewer</option>
                </select>
              </div>
              <div className="role-descriptions">
                <p><strong>Portfolio Admin:</strong> Full access to portfolio, can manage team</p>
                <p><strong>Contractor:</strong> Can edit sensors, devices, settings</p>
                <p><strong>Viewer:</strong> Read-only access to data</p>
              </div>
              <div className="form-actions">
                <button className="btn-primary" onClick={handleAssignPortfolio}>Assign</button>
                <button className="btn-secondary" onClick={() => setAssigningUser(null)}>Cancel</button>
              </div>
            </div>
          </div>
        )}

        {/* Super Admins */}
        <div className="management-section">
          <h2>Super Admins ({superAdmins.length})</h2>
          <p className="hint">Full system access including managing other Super Admins.</p>
          {loadingUsers ? (
            <p>Loading...</p>
          ) : superAdmins.length === 0 ? (
            <p className="empty-msg">No Super Admins</p>
          ) : (
            <div className="users-list">
              {superAdmins.map(u => (
                <UserCard
                  key={u.uid}
                  targetUser={u}
                  currentUser={user}
                  canManageSuperAdmins={canManageSuperAdmins()}
                  userPortfolios={getUserPortfolios(u)}
                  onChangeSystemRole={handleChangeSystemRole}
                  onAssignPortfolio={() => setAssigningUser(u)}
                  onRemoveFromPortfolio={handleRemoveFromPortfolio}
                  onChangePortfolioRole={handleChangePortfolioRole}
                  onDelete={handleDeleteUser}
                  getRoleDisplayName={getRoleDisplayName}
                />
              ))}
            </div>
          )}
        </div>

        {/* Admins */}
        <div className="management-section">
          <h2>Admins ({admins.length})</h2>
          <p className="hint">Full system access except managing Super Admin roles.</p>
          {admins.length === 0 ? (
            <p className="empty-msg">No Admins</p>
          ) : (
            <div className="users-list">
              {admins.map(u => (
                <UserCard
                  key={u.uid}
                  targetUser={u}
                  currentUser={user}
                  canManageSuperAdmins={canManageSuperAdmins()}
                  userPortfolios={getUserPortfolios(u)}
                  onChangeSystemRole={handleChangeSystemRole}
                  onAssignPortfolio={() => setAssigningUser(u)}
                  onRemoveFromPortfolio={handleRemoveFromPortfolio}
                  onChangePortfolioRole={handleChangePortfolioRole}
                  onDelete={handleDeleteUser}
                  getRoleDisplayName={getRoleDisplayName}
                />
              ))}
            </div>
          )}
        </div>

        {/* Regular Users */}
        <div className="management-section">
          <h2>Users ({regularUsers.length})</h2>
          <p className="hint">Users with portfolio-level access only.</p>
          {regularUsers.length === 0 ? (
            <p className="empty-msg">No regular users</p>
          ) : (
            <div className="users-list">
              {regularUsers.map(u => (
                <UserCard
                  key={u.uid}
                  targetUser={u}
                  currentUser={user}
                  canManageSuperAdmins={canManageSuperAdmins()}
                  userPortfolios={getUserPortfolios(u)}
                  onChangeSystemRole={handleChangeSystemRole}
                  onAssignPortfolio={() => setAssigningUser(u)}
                  onRemoveFromPortfolio={handleRemoveFromPortfolio}
                  onChangePortfolioRole={handleChangePortfolioRole}
                  onDelete={handleDeleteUser}
                  getRoleDisplayName={getRoleDisplayName}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      <style>{`
        .user-management {
          height: calc(100vh - 60px);
          background: #16213e;
          display: flex;
          flex-direction: column;
        }
        .management-header {
          display: flex;
          align-items: center;
          gap: 20px;
          padding: 20px;
          background: #1a1a2e;
          border-bottom: 1px solid #3a3a5a;
        }
        .management-header h1 {
          margin: 0;
          color: #fff;
          flex: 1;
        }
        .back-btn {
          background: #3a3a5a;
          border: none;
          color: #fff;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
        }
        .back-btn:hover {
          background: #4a4a6a;
        }
        .management-content {
          flex: 1;
          overflow-y: auto;
          padding: 20px;
          max-width: 1000px;
          margin: 0 auto;
          width: 100%;
        }
        .management-section {
          background: #1a1a2e;
          border-radius: 8px;
          padding: 20px;
          margin-bottom: 20px;
        }
        .management-section h2 {
          margin: 0 0 8px 0;
          color: #4ecdc4;
          font-size: 18px;
        }
        .new-user-form {
          border: 2px solid #4ecdc4;
        }
        .form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 15px;
          margin-bottom: 15px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .form-group label {
          color: #888;
          font-size: 13px;
          font-weight: 600;
        }
        .form-group input,
        .form-group select {
          background: #16213e;
          border: 1px solid #3a3a5a;
          border-radius: 4px;
          padding: 10px 12px;
          color: #fff;
          font-size: 14px;
        }
        .form-group input:focus,
        .form-group select:focus {
          outline: none;
          border-color: #4ecdc4;
        }
        .form-actions {
          display: flex;
          gap: 10px;
          margin-bottom: 10px;
        }
        .form-note {
          font-size: 12px;
          color: #888;
          margin: 0;
        }
        .hint {
          font-size: 13px;
          color: #888;
          margin: 0 0 15px 0;
        }
        .field-hint {
          font-size: 11px;
          color: #666;
          margin: 4px 0 0 0;
        }
        .portfolio-assignments-section {
          background: #16213e;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 20px;
        }
        .portfolio-assignments-section h3 {
          margin: 0 0 8px 0;
          color: #4ecdc4;
          font-size: 16px;
        }
        .add-portfolio-row {
          display: flex;
          gap: 10px;
          margin-bottom: 15px;
        }
        .add-portfolio-row .portfolio-select {
          flex: 2;
          background: #2a2a4a;
          border: 1px solid #3a3a5a;
          border-radius: 4px;
          padding: 8px 12px;
          color: #fff;
          font-size: 14px;
        }
        .add-portfolio-row .role-select {
          flex: 1;
          background: #2a2a4a;
          border: 1px solid #3a3a5a;
          border-radius: 4px;
          padding: 8px 12px;
          color: #fff;
          font-size: 14px;
        }
        .assigned-portfolios {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-bottom: 15px;
        }
        .assigned-portfolio-tag {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #2a2a4a;
          padding: 8px 12px;
          border-radius: 6px;
          border: 1px solid #3a3a5a;
        }
        .assigned-portfolio-tag .portfolio-name {
          color: #fff;
          font-weight: 500;
        }
        .assigned-portfolio-tag select {
          background: #3a3a5a;
          border: none;
          color: #4ecdc4;
          padding: 4px 8px;
          border-radius: 4px;
          font-size: 12px;
          cursor: pointer;
        }
        .assigned-portfolio-tag .remove-btn {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          padding: 0;
          font-size: 18px;
          line-height: 1;
        }
        .assigned-portfolio-tag .remove-btn:hover {
          color: #e74c3c;
        }
        .btn-primary {
          background: #4ecdc4;
          color: #1a1a2e;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
          font-weight: 600;
        }
        .btn-primary:hover {
          background: #3dbdb5;
        }
        .btn-primary:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }
        .btn-secondary {
          background: #3a3a5a;
          color: #fff;
          border: none;
          padding: 10px 20px;
          border-radius: 4px;
          cursor: pointer;
        }
        .btn-secondary:hover {
          background: #4a4a6a;
        }
        .users-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        .user-card {
          background: #16213e;
          border-radius: 8px;
          padding: 15px;
          border: 1px solid #3a3a5a;
        }
        .user-card-header {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 12px;
        }
        .user-info {
          flex: 1;
        }
        .user-name {
          color: #fff;
          font-weight: 600;
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .user-email {
          color: #888;
          font-size: 13px;
        }
        .you-tag {
          background: #4ecdc4;
          color: #1a1a2e;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 10px;
          font-weight: 600;
        }
        .system-role-badge {
          padding: 6px 12px;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 600;
          white-space: nowrap;
        }
        .system-role-badge.super_admin {
          background: #e74c3c;
          color: #fff;
        }
        .system-role-badge.admin {
          background: #9b59b6;
          color: #fff;
        }
        .system-role-badge.none {
          background: #3a3a5a;
          color: #888;
        }
        .user-card-actions {
          display: flex;
          gap: 8px;
        }
        .user-portfolios {
          margin-top: 10px;
          padding-top: 10px;
          border-top: 1px solid #3a3a5a;
        }
        .user-portfolios-header {
          font-size: 12px;
          color: #888;
          margin-bottom: 8px;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
        .portfolio-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
        }
        .portfolio-tag {
          display: flex;
          align-items: center;
          gap: 8px;
          background: #2a2a4a;
          padding: 6px 10px;
          border-radius: 4px;
          font-size: 12px;
        }
        .portfolio-tag .name {
          color: #fff;
        }
        .portfolio-tag select {
          background: #3a3a5a;
          border: none;
          color: #4ecdc4;
          padding: 2px 6px;
          border-radius: 3px;
          font-size: 11px;
          cursor: pointer;
        }
        .portfolio-tag .remove-btn {
          background: none;
          border: none;
          color: #888;
          cursor: pointer;
          padding: 0;
          font-size: 14px;
          line-height: 1;
        }
        .portfolio-tag .remove-btn:hover {
          color: #e74c3c;
        }
        .no-portfolios {
          color: #666;
          font-size: 12px;
          font-style: italic;
        }
        .btn-small {
          background: #3498db;
          color: #fff;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          white-space: nowrap;
        }
        .btn-small:hover {
          background: #2980b9;
        }
        .btn-small.promote {
          background: #9b59b6;
        }
        .btn-small.promote:hover {
          background: #8e44ad;
        }
        .btn-small.assign {
          background: #27ae60;
        }
        .btn-small.assign:hover {
          background: #219a52;
        }
        .btn-danger-small {
          background: #e74c3c;
          color: #fff;
          border: none;
          padding: 6px 12px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          white-space: nowrap;
        }
        .btn-danger-small:hover {
          background: #c0392b;
        }
        .empty-msg {
          color: #888;
          font-style: italic;
        }
        .no-access {
          padding: 40px;
          text-align: center;
          color: #888;
        }
        .modal-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          background: rgba(0,0,0,0.8);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
        }
        .modal {
          background: #2a2a4a;
          border-radius: 8px;
          padding: 25px;
          width: 450px;
          max-width: 90%;
        }
        .modal h3 {
          margin: 0 0 20px 0;
          color: #4ecdc4;
        }
        .modal .form-group {
          margin-bottom: 15px;
        }
        .role-descriptions {
          background: #1a1a2e;
          border-radius: 4px;
          padding: 12px;
          margin-bottom: 15px;
        }
        .role-descriptions p {
          margin: 0 0 6px 0;
          font-size: 12px;
          color: #888;
        }
        .role-descriptions p:last-child {
          margin-bottom: 0;
        }
        .role-descriptions strong {
          color: #4ecdc4;
        }
        @media (max-width: 600px) {
          .form-grid {
            grid-template-columns: 1fr;
          }
          .user-card-header {
            flex-wrap: wrap;
          }
        }
      `}</style>
    </div>
  );
}

// User Card Component
function UserCard({
  targetUser,
  currentUser,
  canManageSuperAdmins,
  userPortfolios,
  onChangeSystemRole,
  onAssignPortfolio,
  onRemoveFromPortfolio,
  onChangePortfolioRole,
  onDelete,
  getRoleDisplayName
}) {
  const isCurrentUser = targetUser.uid === currentUser.uid;
  const isSuperAdmin = targetUser.role === 'super_admin';
  const isAdmin = targetUser.role === 'admin';

  return (
    <div className="user-card">
      <div className="user-card-header">
        <div className="user-info">
          <div className="user-name">
            {targetUser.name || 'No name'}
            {isCurrentUser && <span className="you-tag">You</span>}
          </div>
          <div className="user-email">{targetUser.email}</div>
        </div>

        <span className={`system-role-badge ${targetUser.role || 'none'}`}>
          {targetUser.role ? getRoleDisplayName(targetUser.role) : 'No System Role'}
        </span>

        {!isCurrentUser && (
          <div className="user-card-actions">
            {/* System role changes */}
            {canManageSuperAdmins && !isSuperAdmin && (
              <button
                className="btn-small promote"
                onClick={() => onChangeSystemRole(targetUser, 'super_admin')}
              >
                Make Super Admin
              </button>
            )}
            {!isAdmin && !isSuperAdmin && (
              <button
                className="btn-small"
                onClick={() => onChangeSystemRole(targetUser, 'admin')}
              >
                Make Admin
              </button>
            )}
            {(isSuperAdmin && canManageSuperAdmins) || isAdmin ? (
              <button
                className="btn-small"
                onClick={() => onChangeSystemRole(targetUser, null)}
              >
                Remove System Role
              </button>
            ) : null}
            <button
              className="btn-danger-small"
              onClick={() => onDelete(targetUser)}
            >
              Delete
            </button>
          </div>
        )}
      </div>

      {/* Portfolio memberships */}
      <div className="user-portfolios">
        <div className="user-portfolios-header">
          <span>Portfolio Access</span>
          {!isCurrentUser && (
            <button className="btn-small assign" onClick={onAssignPortfolio}>
              + Assign Portfolio
            </button>
          )}
        </div>
        {userPortfolios.length === 0 ? (
          <p className="no-portfolios">No portfolio access</p>
        ) : (
          <div className="portfolio-tags">
            {userPortfolios.map(p => (
              <div key={p.id} className="portfolio-tag">
                <span className="name">{p.name}</span>
                <select
                  value={p.role}
                  onChange={(e) => onChangePortfolioRole(targetUser, p.id, e.target.value)}
                  disabled={isCurrentUser}
                >
                  <option value="customer_admin">Admin</option>
                  <option value="customer_technician">Contractor</option>
                  <option value="customer_viewer">Viewer</option>
                </select>
                {!isCurrentUser && (
                  <button
                    className="remove-btn"
                    onClick={() => onRemoveFromPortfolio(targetUser, p.id)}
                    title="Remove from portfolio"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default UserManagement;
